import { useEffect, useState } from 'react'
import { apiFetch } from '../../lib/api'
import { useAuthSession } from '../../lib/authSession'
import { termsReviewRows, type TermsReviewItem } from './commitmentReview'

interface ReviewResponse {
  document_id: string
  items: TermsReviewItem[]
}

export default function TermsReviewCard({ documentId }: { documentId: string }) {
  const authSession = useAuthSession()
  const [refresh, setRefresh] = useState(0)
  const [state, setState] = useState<{
    documentId: string
    session: string | null
    response: ReviewResponse | null
    failed: boolean
  } | null>(null)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    const controller = new AbortController()
    setState(null)
    setLoading(true)
    apiFetch<ReviewResponse>(
      `/api/documents/${encodeURIComponent(documentId)}/commitment-reviews`,
      {
        signal: controller.signal,
      }
    )
      .then((response) => {
        if (controller.signal.aborted) return
        if (response.document_id !== documentId || !Array.isArray(response.items)) {
          throw new Error('Review response does not match this document')
        }
        setState({ documentId, session: authSession, response, failed: false })
      })
      .catch(() => {
        if (!controller.signal.aborted) {
          setState({ documentId, session: authSession, response: null, failed: true })
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false)
      })
    return () => controller.abort()
  }, [documentId, authSession, refresh])

  // Do not show a previous document/user's result before the effect clears it.
  if (state?.documentId !== documentId || state.session !== authSession) return null
  const rows = termsReviewRows(state.response?.items ?? [])
  if (!state.failed && rows.length === 0) return null

  return (
    <section
      className="vcard"
      style={{ padding: 16 }}
      aria-label="Terms review"
      data-testid="terms-review-card"
    >
      {state.failed ? (
        <p>Terms review status is unavailable. Refresh to check it.</p>
      ) : (
        rows.map((row) => (
          <div key={row.id} style={{ marginBottom: 12 }}>
            <h3 style={{ margin: '0 0 6px', fontSize: 'var(--text-14)' }}>{row.title}</h3>
            {row.scope && (
              <p style={{ margin: '0 0 6px', fontSize: 'var(--text-12)' }}>{row.scope}</p>
            )}
            {row.reasons.length > 0 && (
              <ul style={{ margin: '6px 0', paddingLeft: 20 }}>
                {row.reasons.map((reason) => (
                  <li key={reason}>{reason}</li>
                ))}
              </ul>
            )}
            <p style={{ margin: '6px 0', fontSize: 'var(--text-12)', color: 'var(--muted)' }}>
              {row.message}
            </p>
          </div>
        ))
      )}
      <button
        type="button"
        className="fa-tlink"
        disabled={loading}
        onClick={() => setRefresh((n) => n + 1)}
      >
        Refresh review status
      </button>
    </section>
  )
}
